/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { Result } from "../../network/http-client"
import { AppRoutes } from "../../routes/routes"
import { isUUID } from "../../utils/is-uuid"
import type { ViewModel } from "../../viewmodel/viewmodel"
import type { ApplicationDetail } from "../application-detail/application-detail-types"
import type { ApplicationDetailViewModel } from "../application-detail/application-detail-viewmodel"

import type { ApplicationSearchService } from "./application-search-service"
import type { ApplicationSearchStore } from "./application-search-store"

export class ApplicationSearchViewmodel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly store: ApplicationSearchStore,
        private readonly service: ApplicationSearchService,
        private readonly getDetailVM: (id: number) => ApplicationDetailViewModel
    ) {}

    hasDataFor(key: string): boolean {
        return this.store.results.has(key)
    }

    searchResults(query: string): ApplicationDetail[] {
        return this.store.results.get(query) ?? []
    }

    // return string where user should be redirected
    // if empty string is returned, we won't redirect
    async searchApplicationByQuery(query: string): Promise<string> {
        if (!query) {
            return ""
        }
        if (isUUID(query)) {
            // search by id and then redirect to proper detail page
            const detail = await this.handleUUIDsearch(query)
            if (detail) {
                return AppRoutes.ApplicationDetail.replace(":id", `${detail.id}`)
            }
            return ""
        }
        // search by client ID, if there is only 1 res
        const appDetails = await this.handleClientUserId(query)
        if (appDetails) {
            if (appDetails.length === 1) {
                return AppRoutes.ApplicationDetail.replace(":id", `${appDetails[0].id}`)
            } else {
                return AppRoutes.ApplicationSearch.replace(":query", `${query}`)
            }
        }
        return ""
    }

    // Service calls
    private async searchApplicationByAvUserId(avUserId: string): Promise<Result<ApplicationDetail>> {
        return await this.service.searchByAvUserId(avUserId)
    }

    // Unused, let's check with Vlnka if we need to lookup using client device id.
    // biome-ignore lint/correctness/noUnusedPrivateClassMembers: kept pending the decision above
    private async searchApplicationByClientDeviceId(clientDeviceId: string): Promise<Result<ApplicationDetail[]>> {
        return await this.service.searchByClientDeviceId(clientDeviceId)
    }

    private async searchApplicationByClientUserId(clientUserId: string): Promise<Result<ApplicationDetail[]>> {
        return await this.service.searchByClientUserId(clientUserId)
    }

    private async handleUUIDsearch(uuid: string): Promise<ApplicationDetail | null> {
        const result = await this.searchApplicationByAvUserId(uuid)
        if (result.success && result.data) {
            const detail = result.data as ApplicationDetail
            if (detail.id) {
                this.getDetailVM(detail.id).setPrefetchedAppDetail(detail)
            }
            return detail
        }
        return null
    }

    private async handleClientUserId(clientUserId: string): Promise<ApplicationDetail[] | null> {
        // check in cache
        if (this.store.results.has(clientUserId)) {
            return this.store.results.get(clientUserId) ?? []
        }
        const result = await this.searchApplicationByClientUserId(clientUserId)
        if (result.success && result.data) {
            const detail = result.data as ApplicationDetail[]

            // if there is only one result, we set prefetched data into viewmodel
            if (detail.length === 1) {
                const first = detail[0]
                if (first.id) {
                    this.getDetailVM(first.id).setPrefetchedAppDetail(first)
                }
                return [first]
            }

            if (detail.length === 0) {
                // no results found
                return []
            }

            if (detail.length > 1) {
                this.store.setResults(clientUserId, detail)
            }

            return detail
        }
        return null
    }
}
